// src/pages/DriftFeed.js
// HOME = Drift. Two views: an immersive fullscreen vertical feed (default,
// mirrors the mobile app) and a grid "Explore". Both pull /api/drift/feed.

import { useCallback, useEffect, useState } from "react";
import MediaCard from "../components/MediaCard";
import Avatar from "../components/Avatar";
import PostDetail from "../components/PostDetail";
import DriftFullscreen from "../components/DriftFullscreen";
import { drift, social } from "../api";

const CATEGORIES = [
  "For you",
  "Trending Motion",
  "Trending Anime",
  "Trending Realism",
  "Trending Dance",
  "Trending Cinematic",
];

export default function DriftFeed({ user, onRequireAuth, onRemix, onOpenCreator }) {
  const [view, setView] = useState("grid"); // "grid" (default) | "feed" (fullscreen)
  const [cat, setCat] = useState("For you");
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [viewing, setViewing] = useState(null);
  const [creators, setCreators] = useState([]);

  useEffect(() => {
    social.trending().then((d) => setCreators(d.creators || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      let data;
      if (cat === "For you") {
        // /drift/feed is auth-only (personalized); logged-out visitors get the
        // public "most remixed" feed so Drift is browsable without an account.
        data = user ? await drift.feed(1) : await drift.mostRemixed();
      } else {
        data = await drift.category(cat); // public
      }
      setPosts(data.posts || []);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, [cat, user]);

  useEffect(() => {
    load();
  }, [load]);

  const like = async (post) => {
    if (!user) return onRequireAuth();
    try {
      const r = await drift.like(post._id);
      setPosts((ps) => ps.map((p) => (p._id === post._id ? { ...p, likes_count: r.likes, likes: r.likes } : p)));
    } catch (_) {}
  };

  const remix = (post) => {
    if (!user) return onRequireAuth();
    onRemix(post);
  };

  const ViewToggle = (
    <div className="drift-toggle">
      <button className={view === "feed" ? "on" : ""} onClick={() => setView("feed")} title="Feed">▤</button>
      <button className={view === "grid" ? "on" : ""} onClick={() => setView("grid")} title="Explore grid">▦</button>
    </div>
  );

  // ---- Fullscreen feed ----
  if (view === "feed") {
    return (
      <div className="drift-wrap">
        {ViewToggle}
        {err && <div className="form-error center">{err}</div>}
        {loading ? (
          <div className="drift-fs"><div className="drift-fs__item drift-fs__item--load" /></div>
        ) : posts.length === 0 ? (
          <div className="empty">No drifts here yet. Be the first to create one.</div>
        ) : (
          <DriftFullscreen
            posts={posts}
            user={user}
            onRequireAuth={onRequireAuth}
            onRemix={remix}
            onOpenCreator={onOpenCreator}
            onOpenComments={setViewing}
          />
        )}
        {viewing && (
          <PostDetail post={viewing} user={user} onClose={() => setViewing(null)} onRequireAuth={onRequireAuth}
            onRemix={remix} onLike={like} onOpenCreator={onOpenCreator}
            onDeleted={(p) => setPosts((ps) => ps.filter((x) => x._id !== p._id))} />
        )}
      </div>
    );
  }

  // ---- Explore grid ----
  return (
    <div className="page">
      <div className="page__head" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <h1>Drift</h1>
          <p className="page__sub">Remix trending AI images &amp; videos — or post your own.</p>
        </div>
        {ViewToggle}
      </div>

      {creators.length > 0 && (
        <div className="creators-strip">
          {creators.map(({ user: c }) => (
            <button key={c.id} className="creators-strip__item" onClick={() => onOpenCreator?.(c.id)}>
              <Avatar name={c.username} url={c.avatar_url} size={54} />
              <span className="creators-strip__name">@{c.username}</span>
              <span className="creators-strip__followers">{c.followers_count || 0} followers</span>
            </button>
          ))}
        </div>
      )}

      <div className="chips chips--scroll">
        {CATEGORIES.map((c) => (
          <button key={c} className={`chip ${cat === c ? "chip--active" : ""}`} onClick={() => setCat(c)}>
            {c.replace("Trending ", "")}
          </button>
        ))}
      </div>

      {err && <div className="form-error center">{err}</div>}

      {loading ? (
        <div className="masonry">
          {Array.from({ length: 9 }).map((_, i) => (
            <div key={i} className="mcard mcard--skeleton" style={{ height: 140 + (i % 3) * 60 }} />
          ))}
        </div>
      ) : posts.length === 0 ? (
        <div className="empty">No drifts here yet. Be the first to create one.</div>
      ) : (
        <div className="masonry">
          {posts.map((p) => (
            <MediaCard key={p._id} post={p} onOpen={setViewing} onLike={like} onRemix={remix} onOpenCreator={onOpenCreator} />
          ))}
        </div>
      )}

      {viewing && (
        <PostDetail post={viewing} user={user} onClose={() => setViewing(null)} onRequireAuth={onRequireAuth}
          onRemix={remix} onLike={like} onOpenCreator={onOpenCreator} />
      )}
    </div>
  );
}
